import SearchForm from "@shared/components/SearchForm";
import { PlusIcon } from "@shared/components/ui/Icon";
import theme from "@shared/utils/themeColors";
import React from "react";
import { MessageSidebarProps } from "../types";
import DirectMessages from "./DirectMessages";

const MessagesSidebar: React.FC<MessageSidebarProps> = ({
  selectedConversation,
  setSelectedConversation,
  msgSidebarOpen,
  setMsgSidebarOpen,
  onOpenCreateGroupModal,
  groups,
  onOpenEditGroupModal,
}) => {
  // State for the search term used to filter users by name
  const [searchTerm, setSearchTerm] = React.useState("");

  return (
    <div
      id="messages-sidebar"
      className={`absolute z-20 top-0 bottom-0 w-full md:w-auto md:static md:top-auto md:bottom-auto -mr-px md:translate-x-0 transition-transform duration-200 ease-in-out ${
        msgSidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div
        className={`sticky top-16 bg-white dark:bg-[#151D2C] overflow-x-hidden overflow-y-auto no-scrollbar shrink-0 border-r ${theme.border.primary} md:w-[18rem] xl:w-[20rem] h-[calc(100dvh-64px)]`}
      >
        {/* #Marketing group */}
        <div>
          {/* Group body */}
          <div className="px-5 py-4">
            {/* Search form and create group button */}
            <div className="relative flex items-center mb-2 gap-2">
              <div className="flex-1 min-w-0">
                <SearchForm placeholder="Search..." onChange={setSearchTerm} />
              </div>
              <button
                type="button"
                className="rounded-full h-9 w-9 bg-slate-100 hover:bg-blue-100 text-blue-600 flex items-center justify-center"
                onClick={onOpenCreateGroupModal}
                title="Create group"
                style={{ minWidth: 0 }}
              >
                <PlusIcon size={20} className="h-6 w-6" />
              </button>
            </div>

            {/* Direct messages */}
            <DirectMessages
              setMsgSidebarOpen={setMsgSidebarOpen}
              selectedConversation={selectedConversation}
              setSelectedConversation={setSelectedConversation}
              searchTerm={searchTerm}
            />

            {/* Group chats */}
            <div className="mt-8">
              <div
                className={`text-xs font-semibold text-gray-400 ${theme.text.muted}`}
              >
                Groups
              </div>
              <ul className="mb-6">
                {groups
                  .filter((group) => {
                    if (!searchTerm || searchTerm.trim() === "") return true;
                    return group.title
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase());
                  })
                  .map((group) => (
                    <li className="-mx-2" key={group.id}>
                      <button
                        className={`flex items-center justify-between w-full p-2 rounded-lg ${
                          selectedConversation === group.id
                            ? `bg-[linear-gradient(135deg,var(--tw-gradient-stops))] ${theme.bg.gradientPrimary}`
                            : "hover:bg-slate-50 dark:hover:bg-slate-800"
                        }`}
                        onClick={() => {
                          setMsgSidebarOpen(false);
                          setSelectedConversation(group.id);
                        }}
                      >
                        <div className="flex items-center truncate">
                          {/* Hashtag icon with group color */}
                          <span
                            className="flex items-center justify-center w-8 h-8 rounded-full mr-2"
                            style={{ backgroundColor: "#111827" }}
                          >
                            <svg
                              width="20"
                              height="20"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke={group.color || "#6366f1"}
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M7 17l2-10m4 10l2-10M5 9h14M5 15h14"
                              />
                            </svg>
                          </span>
                          <div>
                            <div className="truncate text-left">
                              <span
                                className={`text-sm font-medium ${theme.text.primary}`}
                              >
                                {group.title}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center ml-2">
                          {group.unreadCount > 0 && (
                            <div
                              className={`text-xs inline-flex font-medium ${theme.bg.badge} ${theme.text.white} rounded-full text-center leading-5 px-2`}
                            >
                              {group.unreadCount}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesSidebar;
